import { useMemo } from 'react'
import { Box, Grid, HStack, Text, VStack } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import type { CalendarEvent } from '../mock-data'
import { DATE_KEY, groupByDate, monthGridStart } from '../calendar-layout'

// The month body of the dashboard calendar.
//
// A cell is too narrow for a card, so each event is a coloured bar in its team's
// accent and the day count carries the rest. Tapping a day hands the date back so
// the panel can drop into that day's timeline, which is where the detail lives.

interface ICalendarMonthGridProps {
  events: CalendarEvent[]
  selectedDate: Date
  /** Selecting a day — the panel switches to the day timeline. */
  onSelectDay: (date: Date) => void
  /** Month chevrons — moves the selection without leaving month mode. */
  onChangeMonth: (date: Date) => void
}

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
/** Always six rows, so the grid's height does not jump between months. */
const WEEK_ROWS = 6
const CELL_COUNT = WEEK_ROWS * 7
/** Beyond this the bars stop being countable and start being noise. */
const MAX_BARS_PER_DAY = 3
const ACCENT = '#4DB3FF'

const MonthNavButton = ({
  label,
  onClick,
  children
}: {
  label: string
  onClick: () => void
  children: JSX.Element
}) => (
  <Box
    as="button"
    type="button"
    aria-label={label}
    onClick={onClick}
    border="none"
    bg="transparent"
    color="rgba(255,255,255,0.65)"
    cursor="pointer"
    p="8px"
    display="flex"
    alignItems="center"
    _focus={{ boxShadow: 'none' }}
    _focusVisible={{ boxShadow: 'none' }}
  >
    {children}
  </Box>
)

const CalendarMonthGrid = ({ events, selectedDate, onSelectDay, onChangeMonth }: ICalendarMonthGridProps) => {
  const selected = dayjs(selectedDate)
  // Keyed on the month string: `selected` is a new dayjs object every render, so
  // depending on it directly would rebuild all 42 cells each time.
  const monthKey = selected.format('YYYY-MM')

  const eventsByDate = useMemo(() => groupByDate(events), [events])
  const cells = useMemo(() => {
    const start = monthGridStart(dayjs(`${monthKey}-01`))
    return Array.from({ length: CELL_COUNT }, (_, index) => start.add(index, 'day'))
  }, [monthKey])

  const today = dayjs()

  return (
    <VStack align="stretch" spacing="8px">
      <HStack justify="space-between">
        <MonthNavButton
          label="Previous month"
          onClick={() => onChangeMonth(selected.subtract(1, 'month').startOf('month').toDate())}
        >
          <FiChevronLeft size={18} />
        </MonthNavButton>
        <Text fontSize="14px" fontWeight={700} color="white" m={0}>
          {selected.format('MMMM YYYY')}
        </Text>
        <MonthNavButton
          label="Next month"
          onClick={() => onChangeMonth(selected.add(1, 'month').startOf('month').toDate())}
        >
          <FiChevronRight size={18} />
        </MonthNavButton>
      </HStack>

      <Grid templateColumns="repeat(7, 1fr)" gap="2px">
        {DAY_LABELS.map((label, index) => (
          <Text
            key={`${label}-${index}`}
            textAlign="center"
            fontSize="11px"
            fontWeight={600}
            color="rgba(255,255,255,0.45)"
            m={0}
          >
            {label}
          </Text>
        ))}
      </Grid>

      <Grid templateColumns="repeat(7, 1fr)" gap="4px">
        {cells.map(day => {
          const iso = day.format(DATE_KEY)
          const dayEvents = eventsByDate.get(iso) ?? []
          const isCurrentMonth = day.isSame(selected, 'month')
          const isToday = day.isSame(today, 'day')
          const isSelected = day.isSame(selected, 'day')
          const overflow = dayEvents.length - MAX_BARS_PER_DAY

          return (
            <VStack
              key={iso}
              as="button"
              type="button"
              onClick={() => onSelectDay(day.toDate())}
              spacing="3px"
              align="stretch"
              minH="72px"
              px="6px"
              pt="6px"
              pb="5px"
              borderRadius="8px"
              cursor="pointer"
              bg="transparent"
              // A hairline on every cell so a day with nothing in it still reads as
              // a day; the selected one upgrades to the accent instead of adding a
              // second edge on top.
              border="1px solid"
              borderColor={isSelected ? ACCENT : 'rgba(255,255,255,0.08)'}
              _focus={{ boxShadow: 'none' }}
              _focusVisible={{ boxShadow: 'none' }}
            >
              <Text
                alignSelf="flex-start"
                fontSize="12px"
                lineHeight="16px"
                m={0}
                fontWeight={isToday || isSelected ? 700 : 500}
                color={isToday ? ACCENT : isCurrentMonth ? 'white' : 'rgba(255,255,255,0.35)'}
              >
                {day.format('D')}
              </Text>

              {dayEvents.slice(0, MAX_BARS_PER_DAY).map(event => (
                <Box key={event.id} height="4px" borderRadius="2px" bg={event.teamTag.color} />
              ))}

              {overflow > 0 && (
                <Text fontSize="10px" lineHeight="12px" textAlign="center" color="rgba(255,255,255,0.55)" m={0}>
                  +{overflow}
                </Text>
              )}
            </VStack>
          )
        })}
      </Grid>
    </VStack>
  )
}

export default CalendarMonthGrid
